import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { ncdAPI } from "../../../../services/ncd.api";
import { getLocationFacilityDetails } from "../../utils/constants";
import {
    TextField,
    Button,
    FormControl,
    Select,
    MenuItem,
    Box,
    Grid,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Dialog,
    DialogContent,
    DialogActions
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const MEDICINE_OPTIONS = [
    "Lignocaine Topical forms 2-5%",
    "Diclofenac 50mg",
    "Diclofenac Injection 25mg/ml",
    "Paracetamol 500mg",
    "Paracetamol 650mg",
    "Paracetamol liquid",
    "Ciprofloxacin 10mg",
    "Ciprofloxacin 500mg",
    "Gentamicin 10mg/ml",
    "Gentamicin 40mg/ml",
    "Metronidazole 200mg",
    "Metronidazole 400mg",
    "Amoxicillin 250mg",
    "Amoxicillin 500mg",
    "Amoxicillin liquid 250mg/5ml",
    "Fluconazole 100mg",
    "Clotrimazole Cream 1%",
    "Methylrosanilinium chloride(Gentian Violet). Topical Preparation 0.25% to 2%",
    "Ciprofloxacin Drops 0.3%",
    "Ciprofloxacin Drops Ointment 0.3%",
    "Acetylsalicylic Acid 300mg to 500mg",
    "Acetylsalicylic Acid. Effervescent/ Dispersible/ Enteric coated 300mg to 500mg",
    "Ibuprofen 200mg",
    "Ibuprofen 400mg",
    "Ibuprofen liquid 100mg/5ml",
    "Mefenamic acid 250mg",
    "Mefenamic acid 500mg",
    "Clotrimazole Drops 1%",
    "Wax-solvent ear drops : benzocaine, chlorbutol, paradichlorobenzene, turpentine oil"
];

const DhChcReviewView = ({
    facilityType,
    screeningStatus,
    formData,
    encounterId,
    individualId,
    screeningId,
    cervicalScreeningId,
    breastScreeningId,
    oralScreeningId,
    conditionId = 200,
    reviewObservations,
    setReviewObservations,
    treatmentType,
    setTreatmentType,
    treatmentOther,
    setTreatmentOther,
    followupReason,
    setFollowupReason,
    followupTo,
    setFollowupTo,
    durationDays,
    durationWeeks,
    durationMonths,
    handleDurationChange,
    dueDate,
    setDueDate,
    referTo,
    setReferTo,
    referReason,
    setReferReason,
    referralRemarks,
    setReferralRemarks,
    handleSaveReview,
    setActiveWorkflowTab
}) => {
    const [treatmentsList, setTreatmentsList] = useState([]);
    const [openMedicalModal, setOpenMedicalModal] = useState(false);
    const [selectedMedicalItem, setSelectedMedicalItem] = useState(null);
    const [popupMedicines, setPopupMedicines] = useState([
        { id: 1, drug: '', durationDays: '', frequency: '', quantity: '', specialInstruction: '' }
    ]);

    const [localTreatmentType, setLocalTreatmentType] = useState('');
    const [localTreatmentOther, setLocalTreatmentOther] = useState('');

    const currentTreatmentType = treatmentType !== undefined && treatmentType !== null ? treatmentType : localTreatmentType;
    const currentTreatmentOther = treatmentOther !== undefined && treatmentOther !== null ? treatmentOther : localTreatmentOther;

    const handleSetTreatmentType = (val) => {
        if (setTreatmentType) setTreatmentType(val);
        setLocalTreatmentType(val);
    };

    const handleSetTreatmentOther = (val) => {
        if (setTreatmentOther) setTreatmentOther(val);
        setLocalTreatmentOther(val);
    };

    useEffect(() => {
        const fetchTreatmentData = async () => {
            const activeScreeningId = screeningId || breastScreeningId || cervicalScreeningId || oralScreeningId;
            if (!individualId || !activeScreeningId) return;

            try {
                const response = await ncdAPI.getScreeningTreatment(conditionId, individualId, activeScreeningId);
                const data = response?.data || response;
                if (data) {
                    if (Array.isArray(data.treatment) && data.treatment.length > 0) {
                        const list = data.treatment.map((t, idx) => ({
                            id: t.id || Date.now() + idx,
                            orderedDate: t.orderedDate || dayjs(formData?.assessmentDate).format('DD-MM-YYYY'),
                            treatmentType: t.type || t.treatmentType || '',
                            dateOfTreatment: t.treatmentDate || t.surgeryDate || t.surgryDate || t.orderedDate || dayjs(formData?.assessmentDate).format('DD-MM-YYYY'),
                            status: t.status || (t.treatmentCompleted ? 'Completed' : 'Pending'),
                            medicineMangementMedList: Array.isArray(t.medicineMangementMedList) ? t.medicineMangementMedList : []
                        }));
                        setTreatmentsList(list);

                        // Extract medicineMangementMedList from medical treatment item or medicines array
                        const medicalItem = Array.isArray(data.treatment) ? data.treatment.find(t => (t.type && (t.type === 'Medical Management' || t.type.startsWith('Medical Management'))) || (t.treatmentType && t.treatmentType.startsWith('Medical Management')) || (Array.isArray(t.medicineMangementMedList) && t.medicineMangementMedList.length > 0)) : null;
                        const medListSource = (medicalItem && Array.isArray(medicalItem.medicineMangementMedList) && medicalItem.medicineMangementMedList.length > 0)
                            ? medicalItem.medicineMangementMedList
                            : (Array.isArray(data.medicines) && data.medicines.length > 0 ? data.medicines : null);

                        if (medListSource && medListSource.length > 0) {
                            setPopupMedicines(medListSource.map((m, i) => ({
                                id: i + 1,
                                drug: m.name || m.drug || m.medicineName || m.medicine || '',
                                durationDays: m.dur !== undefined && m.dur !== null ? String(m.dur) : (m.durationDays || m.duration || ''),
                                frequency: m.freq || m.frequency || '',
                                quantity: m.quan !== undefined && m.quan !== null ? String(m.quan) : (m.quantity || ''),
                                specialInstruction: m.spInst || m.specialInstruction || m.instruction || ''
                            })));
                        }
                    }

                    if (data.reviewComments || data.reviewObservations) {
                        if (setReviewObservations) setReviewObservations(data.reviewComments || data.reviewObservations);
                    }

                    if (data.refer) {
                        if (setReferTo && data.refer.referredToFacility) setReferTo(String(data.refer.referredToFacility));
                        if (setReferReason && data.refer.referralReason) setReferReason(data.refer.referralReason);
                        if (setReferralRemarks && data.refer.comments) setReferralRemarks(data.refer.comments);
                    }

                    if (data.followUp) {
                        if (setFollowupReason && data.followUp.followUpReason) setFollowupReason(data.followUp.followUpReason);
                        if (setDueDate && data.followUp.followUpDueDate) setDueDate(data.followUp.followUpDueDate);
                        if (setFollowupTo && data.followUp.toFacility) {
                            const fac = data.followUp.toFacility;
                            setFollowupTo(fac === 3400 ? 'DH' : fac === 3300 ? 'CHC' : fac === 3200 ? 'PHC' : 'Self');
                        }
                        if (data.followUp.followUpDuration && handleDurationChange) {
                            const parts = String(data.followUp.followUpDuration).split(';');
                            if (parts.length >= 3) {
                                handleDurationChange('days', parts[0]);
                                handleDurationChange('weeks', parts[1]);
                                handleDurationChange('months', parts[2]);
                            }
                        }
                    }
                }
            } catch (err) {
                console.error("Failed to fetch treatment data:", err);
            }
        };

        fetchTreatmentData();
    }, [conditionId, individualId, screeningId, cervicalScreeningId, breastScreeningId, oralScreeningId]);

    const handleAddTreatment = () => {
        if (!currentTreatmentType) {
            toast.error("Please select treatment");
            return;
        }
        const newEntry = {
            id: Date.now(),
            orderedDate: dayjs(formData?.assessmentDate).format('DD-MM-YYYY'),
            treatmentType: currentTreatmentType === 'Other' && currentTreatmentOther ? `Other: ${currentTreatmentOther}` : currentTreatmentType,
            dateOfTreatment: dayjs(formData?.assessmentDate).format('DD-MM-YYYY'),
            status: 'Pending',
            medicineMangementMedList: []
        };
        setTreatmentsList(prev => [...prev, newEntry]);

        handleSetTreatmentType('');
        handleSetTreatmentOther('');
    };

    const handleDeleteTreatment = (id) => {
        setTreatmentsList(prev => prev.filter(item => item.id !== id));
    };

    const handleUpdateTreatmentDate = (id, newDate) => {
        setTreatmentsList(prev => prev.map(item => item.id === id ? { ...item, dateOfTreatment: newDate } : item));
    };

    const handleUpdateTreatmentStatus = (id, newStatus) => {
        setTreatmentsList(prev => prev.map(item => {
            if (item.id === id) {
                const updated = { ...item, status: newStatus };
                if (selectedMedicalItem && selectedMedicalItem.id === id) {
                    setSelectedMedicalItem(updated);
                }
                return updated;
            }
            return item;
        }));
    };

    // Popup Medicines logic
    const handleAddPopupMedicineRow = () => {
        setPopupMedicines(prev => [
            ...prev,
            { id: Date.now(), drug: '', durationDays: '', frequency: '', quantity: '', specialInstruction: '' }
        ]);
    };

    const handleDeletePopupMedicineRow = (index) => {
        setPopupMedicines(prev => prev.filter((_, i) => i !== index));
    };

    const getFrequencyMultiplier = (freq) => {
        if (!freq) return 1;
        const lower = String(freq).toLowerCase();
        if (lower.includes('four') || lower.includes('4')) return 4;
        if (lower.includes('thrice') || lower.includes('three') || lower.includes('3')) return 3;
        if (lower.includes('twice') || lower.includes('two') || lower.includes('2')) return 2;
        if (lower.includes('once') || lower.includes('one') || lower.includes('1')) return 1;
        return 1;
    };

    const handleUpdatePopupMedicine = (index, field, value) => {
        setPopupMedicines(prev => {
            const updated = [...prev];
            const currentItem = { ...updated[index], [field]: value };

            if (field === 'durationDays' || field === 'frequency') {
                const dur = Number(currentItem.durationDays);
                const freq = currentItem.frequency;
                if (!isNaN(dur) && dur > 0 && freq) {
                    const multiplier = getFrequencyMultiplier(freq);
                    currentItem.quantity = String(dur * multiplier);
                }
            }

            updated[index] = currentItem;
            return updated;
        });
    };

    const isMedicalCompleted = selectedMedicalItem
        ? (selectedMedicalItem.status === 'Completed' || String(selectedMedicalItem.status).toLowerCase() === 'completed' || selectedMedicalItem.treatmentCompleted === true)
        : false;

    const handleSavePopupMedicines = () => {
        if (selectedMedicalItem) {
            const formattedMeds = popupMedicines.filter(m => m.drug).map(m => ({
                name: m.drug,
                drug: m.drug,
                dur: Number(m.durationDays) || 0,
                durationDays: m.durationDays,
                freq: m.frequency || "",
                frequency: m.frequency || "",
                quan: Number(m.quantity) || 0,
                quantity: m.quantity,
                spInst: m.specialInstruction || null,
                specialInstruction: m.specialInstruction || null
            }));
            setTreatmentsList(prev => prev.map(t => t.id === selectedMedicalItem.id ? { ...t, medicineMangementMedList: formattedMeds } : t));
        }
        toast.success("Medication details saved successfully");
        setOpenMedicalModal(false);
    };

    const onSaveClick = () => {
        const loggedData = JSON.parse(localStorage.getItem("loggedinUserdetails")) || JSON.parse(localStorage.getItem("NCD_loggedinUserdetails")) || {};
        const fromFacility = Number(loggedData.facilityId || loggedData.facilityTypeId) || 3400;

        const formattedTreatments = treatmentsList.map(item => {
            const isSurgery = item.treatmentType.includes("Surgery") || item.treatmentType.includes("LEEP");
            const isMedical = item.treatmentType.includes("Medical Management");

            const itemMeds = (selectedMedicalItem && selectedMedicalItem.id === item.id) ? popupMedicines : (item.medicineMangementMedList || []);
            const medList = isMedical ? itemMeds.filter(m => m.drug || m.name).map(m => ({
                name: m.drug || m.name || "",
                dur: Number(m.durationDays || m.dur) || 0,
                freq: m.frequency || m.freq || "",
                quan: Number(m.quantity || m.quan) || 0,
                spInst: m.specialInstruction || m.spInst || null,
                presel: false
            })) : [];

            const obj = {
                type: item.treatmentType,
                orderedDate: item.orderedDate || dayjs(formData?.assessmentDate).format('DD-MM-YYYY'),
                status: item.status || "Pending",
                treatmentDate: isSurgery ? null : (item.dateOfTreatment || dayjs(formData?.assessmentDate).format('DD-MM-YYYY')),
                surgryDate: null,
                dischrgDate: null,
                medicineMangementMedList: medList,
                dischargeDate: null,
                treatmentCompleted: item.status === 'Completed'
            };

            if (isSurgery) {
                obj.surgeryDate = item.dateOfTreatment || dayjs(formData?.assessmentDate).format('DD-MM-YYYY');
            }

            return obj;
        });

        const referObj = (referTo || referReason) ? {
            referredDate: dayjs(formData?.assessmentDate).format('DD-MM-YYYY'),
            referredToFacility: Number(referTo) || 3500,
            referredFromFacility: fromFacility,
            referralReason: referReason || null,
            comments: referralRemarks || null
        } : null;

        const formatDueDate = (dateStr) => {
            if (!dateStr) return dayjs(formData?.assessmentDate).format('DD-MM-YYYY');
            if (dayjs(dateStr, 'DD-MM-YYYY', true).isValid()) return dateStr;
            const parsed = dayjs(dateStr);
            return parsed.isValid() ? parsed.format('DD-MM-YYYY') : dateStr;
        };

        const dDays = parseInt(durationDays) || 0;
        const dWeeks = parseInt(durationWeeks) || 0;
        const dMonths = parseInt(durationMonths) || 0;

        let computedDueDate = dueDate;
        if (dDays > 0 || dWeeks > 0 || dMonths > 0) {
            const baseStr = formData?.assessmentDate;
            let bDate = baseStr ? dayjs(baseStr, ['DD-MM-YYYY', 'YYYY-MM-DD']) : dayjs();
            if (!bDate.isValid()) bDate = dayjs();

            if (dDays > 0) bDate = bDate.add(dDays, 'day');
            if (dWeeks > 0) bDate = bDate.add(dWeeks * 7, 'day');
            if (dMonths > 0) bDate = bDate.add(dMonths, 'month');
            computedDueDate = bDate.format('DD-MM-YYYY');
        } else {
            computedDueDate = formatDueDate(dueDate);
        }

        const followUpObj = (followupReason || dueDate || durationDays || durationWeeks || durationMonths) ? {
            followUpDueDate: computedDueDate,
            followUpDuration: `${durationDays || 0};${durationWeeks || 0};${durationMonths || 0}`,
            followUpReason: followupReason || null,
            fromFacility: fromFacility,
            toFacility: followupTo === 'DH' ? 3400 : followupTo === 'CHC' ? 3300 : followupTo === 'PHC' ? 3200 : fromFacility,
            fromUser: loggedData.userName || loggedData.userId || "doc_h_1290093"
        } : null;

        const payload = {
            assessmentDate: dayjs(formData?.assessmentDate).format('DD-MM-YYYY'),
            treatment: formattedTreatments.length > 0 ? formattedTreatments : null,
            medicines: null,
            cryotherapyObservation: null,
            refer: referObj,
            followUp: followUpObj,
            reviewComments: reviewObservations || null,
            encounterId: formData?.encounterId || encounterId || null
        };

        if (handleSaveReview) {
            handleSaveReview(payload);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Status and Quick Links container */}
            <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, borderBottom: '1px solid #eef2f5', bgcolor: '#fafafa', mb: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: '15px' }}>
                            Status: {screeningStatus || `Diagnosed at ${facilityType || 'DH'}`}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#3f51b5',
                            color: '#fff',
                            fontSize: '9px',
                            fontWeight: 'bold',
                            px: 0.5,
                            py: 0.2,
                            borderRadius: '2px',
                            lineHeight: 1
                        }}>
                            PDF
                        </Box>
                        <Typography sx={{ fontSize: '12px', color: '#3f51b5', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>
                            Quick Links
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* FOLLOW UP Card */}
                    <Box
                        onClick={() => setActiveWorkflowTab && setActiveWorkflowTab(3)}
                        sx={{
                            width: '95px',
                            height: '80px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            bgcolor: '#fff',
                            transition: 'all 0.2s',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
                            '&:hover': { bgcolor: '#f8fafc', borderColor: '#3f51b5' }
                        }}
                    >
                        <img
                            src="/images/Foloowup.png"
                            alt="FOLLOW UP"
                            style={{ width: '32px', height: '32px', marginBottom: '4px', objectFit: 'contain' }}
                        />
                        <Typography sx={{ fontSize: '10px', fontWeight: 800, color: '#3f51b5' }}>
                            FOLLOW UP
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Review Observations Card */}
            <Box sx={{ bgcolor: '#fff', border: '1px solid #cbd5e1', borderRadius: '4px', p: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', pb: 2, mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '16px' }}>
                        Review Observations
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                            variant="standard"
                            value={dayjs(formData?.assessmentDate).format('DD-MM-YYYY')}
                            InputProps={{
                                readOnly: true,
                                endAdornment: <CalendarTodayIcon sx={{ fontSize: '18px', color: '#64748b', ml: 1 }} />
                            }}
                            sx={{ width: '135px', '& .MuiInput-input': { fontWeight: 600, color: '#334155' } }}
                        />
                    </Box>
                </Box>
                <TextField
                    multiline
                    rows={3}
                    fullWidth
                    placeholder=""
                    value={reviewObservations || ''}
                    onChange={(e) => setReviewObservations && setReviewObservations(e.target.value)}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '4px',
                            bgcolor: '#fff',
                            '& fieldset': { borderColor: '#cbd5e1' },
                            '&:hover fieldset': { borderColor: '#94a3b8' },
                            '&.Mui-focused fieldset': { borderColor: '#3f51b5' }
                        }
                    }}
                />
            </Box>

            {/* Treatment Card */}
            <Box sx={{ bgcolor: '#fff', border: '1px solid #cbd5e1', borderRadius: '4px', p: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '16px', mb: 2 }}>
                    Treatment
                </Typography>

                {/* 3-part Segment Box */}
                <Box sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '4px',
                    bgcolor: '#fafafa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    overflow: 'hidden'
                }}>
                    {/* Dropdown */}
                    <Box sx={{ p: 1.5, flex: 1, minWidth: '220px', borderRight: { md: '1px solid #e2e8f0' } }}>
                        <FormControl fullWidth size="small" variant="outlined">
                            <Select
                                value={currentTreatmentType || ''}
                                onChange={(e) => handleSetTreatmentType(e.target.value)}
                                displayEmpty
                                sx={{
                                    bgcolor: '#fff',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    '& fieldset': { borderColor: '#cbd5e1' },
                                    '&:hover fieldset': { borderColor: '#94a3b8' }
                                }}
                            >
                                <MenuItem value="">Select</MenuItem>
                                <MenuItem value="Medical Management">Medical Management</MenuItem>
                                <MenuItem value="Chemotherapy">Chemotherapy</MenuItem>
                                <MenuItem value="Surgery-Mastectomy">Surgery-Mastectomy</MenuItem>
                                <MenuItem value="Surgery-Lumpectomy">Surgery-Lumpectomy</MenuItem>
                                <MenuItem value="Radiotherapy">Radiotherapy</MenuItem>
                                <MenuItem value="Hormonotherapy">Hormonotherapy</MenuItem>
                                <MenuItem value="Other">Other</MenuItem>
                            </Select>
                        </FormControl>
                        {currentTreatmentType === 'Other' && (
                            <TextField
                                size="small"
                                fullWidth
                                variant="outlined"
                                placeholder="Other Treatment Remarks"
                                value={currentTreatmentOther || ''}
                                onChange={(e) => handleSetTreatmentOther(e.target.value)}
                                sx={{ mt: 1, bgcolor: '#fff' }}
                            />
                        )}
                    </Box>

                    {/* + TREATMENT Button */}
                    <Box sx={{ p: 1.5, flex: 1, display: 'flex', justifyContent: 'center', borderRight: { md: '1px solid #e2e8f0' } }}>
                        <Button
                            onClick={handleAddTreatment}
                            variant="contained"
                            sx={{
                                bgcolor: '#3182ce',
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: '13px',
                                px: 3,
                                py: 0.9,
                                borderRadius: '4px',
                                textTransform: 'uppercase',
                                '&:hover': { bgcolor: '#2b6cb0' }
                            }}
                        >
                            + TREATMENT
                        </Button>
                    </Box>

                    {/* Date Display */}
                    <Box sx={{ p: 1.5, flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 600, color: '#334155', fontSize: '14px' }}>
                            {dayjs(formData?.assessmentDate).format('DD-MM-YYYY')}
                        </Typography>
                    </Box>
                </Box>

                {/* List of Treatments Table */}
                {treatmentsList.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '16px', mb: 1.5 }}>
                            List of Treatments
                        </Typography>
                        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '4px', overflow: 'hidden', width: '100%' }}>
                            {/* Table Header */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                bgcolor: '#eef2f6',
                                px: 3,
                                py: 1.8,
                                borderBottom: '1px solid #e2e8f0',
                                width: '100%'
                            }}>
                                <Typography sx={{ width: '22%', fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>
                                    Ordered Date
                                </Typography>
                                <Typography sx={{ width: '30%', fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>
                                    Treatment Type
                                </Typography>
                                <Typography sx={{ width: '28%', fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>
                                    Date
                                </Typography>
                                <Typography sx={{ width: '20%', fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>
                                    Status
                                </Typography>
                            </Box>

                            {/* Table Body Rows */}
                            {treatmentsList.map((item) => {
                                const isMedical = item.treatmentType === 'Medical Management' || item.treatmentType.startsWith('Medical Management');
                                const isCompleted = item.status === 'Completed';
                                const isSurgery = item.treatmentType && (item.treatmentType.includes("Surgery") || item.treatmentType.includes("LEEP"));
                                return (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            px: 3,
                                            py: 2,
                                            borderBottom: '1px solid #f1f5f9',
                                            width: '100%',
                                            '&:last-child': { borderBottom: 'none' }
                                        }}
                                    >
                                        {/* Column 1: Ordered Date */}
                                        <Typography sx={{ width: '22%', fontSize: '14px', color: '#334155', fontWeight: 500 }}>
                                            {item.orderedDate}
                                        </Typography>

                                        {/* Column 2: Treatment Type */}
                                        <Typography
                                            onClick={() => {
                                                if (isMedical) {
                                                    setSelectedMedicalItem(item);
                                                    const itemMeds = Array.isArray(item.medicineMangementMedList) && item.medicineMangementMedList.length > 0
                                                        ? item.medicineMangementMedList
                                                        : [];
                                                    if (itemMeds.length > 0) {
                                                        setPopupMedicines(itemMeds.map((m, i) => ({
                                                            id: i + 1,
                                                            drug: m.name || m.drug || m.medicineName || m.medicine || '',
                                                            durationDays: m.dur !== undefined && m.dur !== null ? String(m.dur) : (m.durationDays || m.duration || ''),
                                                            frequency: m.freq || m.frequency || '',
                                                            quantity: m.quan !== undefined && m.quan !== null ? String(m.quan) : (m.quantity || ''),
                                                            specialInstruction: m.spInst || m.specialInstruction || m.instruction || ''
                                                        })));
                                                    } else {
                                                        setPopupMedicines([
                                                            { id: 1, drug: '', durationDays: '', frequency: '', quantity: '', specialInstruction: '' }
                                                        ]);
                                                    }
                                                    setOpenMedicalModal(true);
                                                }
                                            }}
                                            sx={{
                                                width: '30%',
                                                fontSize: '14px',
                                                fontWeight: 700,
                                                color: isMedical ? '#2563eb' : '#1e293b',
                                                cursor: isMedical ? 'pointer' : 'default',
                                                '&:hover': {
                                                    textDecoration: isMedical ? 'underline' : 'none'
                                                }
                                            }}
                                        >
                                            {item.treatmentType}
                                        </Typography>

                                        {/* Column 3: Date */}
                                        <Box sx={{ width: '28%', display: 'flex', flexDirection: 'column', pr: 2 }}>
                                            <Typography sx={{ fontSize: '11px', color: '#64748b', fontWeight: 600, mb: 0.5 }}>
                                                {isSurgery ? 'Date of Surgery' : 'Date of Treatment'}
                                            </Typography>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    format="DD-MM-YYYY"
                                                    value={item.dateOfTreatment ? dayjs(item.dateOfTreatment, 'DD-MM-YYYY') : null}
                                                    disabled={isCompleted}
                                                    onChange={(newValue) => {
                                                        if (!isCompleted && newValue) {
                                                            handleUpdateTreatmentDate(item.id, newValue.format('DD-MM-YYYY'));
                                                        }
                                                    }}
                                                    slotProps={{
                                                        textField: {
                                                            variant: 'standard',
                                                            size: 'small',
                                                            disabled: isCompleted,
                                                            sx: {
                                                                width: '140px',
                                                                '& .MuiInput-underline:before': { borderBottomColor: isCompleted ? '#e2e8f0' : '#cbd5e1' },
                                                                '& .MuiInputBase-input.Mui-disabled': { color: '#94a3b8', WebkitTextFillColor: '#94a3b8' }
                                                            }
                                                        }
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </Box>

                                        {/* Column 4: Status + Delete */}
                                        <Box sx={{ width: '20%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
                                            <FormControl variant="standard" sx={{ minWidth: '110px' }}>
                                                <Select
                                                    value={item.status || 'Pending'}
                                                    disabled={isCompleted}
                                                    onChange={(e) => !isCompleted && handleUpdateTreatmentStatus(item.id, e.target.value)}
                                                    disableUnderline
                                                    sx={{
                                                        fontSize: '14px',
                                                        fontWeight: 600,
                                                        color: '#475569',
                                                        bgcolor: isCompleted ? '#eef2f6' : 'transparent',
                                                        px: isCompleted ? 1 : 0,
                                                        py: isCompleted ? 0.5 : 0,
                                                        borderRadius: isCompleted ? '4px' : '0px',
                                                        '&.Mui-disabled': {
                                                            color: '#475569',
                                                            WebkitTextFillColor: '#475569'
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="Pending">Pending</MenuItem>
                                                    <MenuItem value="Completed">Completed</MenuItem>
                                                    <MenuItem value="In Progress">In Progress</MenuItem>
                                                </Select>
                                            </FormControl>
                                            {!isCompleted ? (
                                                <IconButton onClick={() => handleDeleteTreatment(item.id)} size="small" sx={{ color: '#2563eb' }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            ) : (
                                                <Box sx={{ width: '28px' }} />
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Medical Management Medication Dialog */}
            <Dialog
                open={openMedicalModal}
                onClose={() => setOpenMedicalModal(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '6px', p: 1 }
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, py: 2, borderBottom: '1px solid #e2e8f0' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '16px' }}>
                        Medical Management
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography sx={{ fontWeight: 600, color: '#475569', fontSize: '14px' }}>
                            {dayjs(formData?.assessmentDate).format('DD-MM-YYYY')}
                        </Typography>
                        <IconButton onClick={() => { setOpenMedicalModal(false); setSelectedMedicalItem(null); }} size="small" sx={{ color: '#2563eb', fontWeight: 'bold' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>

                <DialogContent sx={{ p: 3 }}>
                    {/* ADD MEDICINE Button */}
                    {!isMedicalCompleted && (
                        <Button
                            onClick={handleAddPopupMedicineRow}
                            variant="contained"
                            sx={{
                                bgcolor: '#0d9488',
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: '13px',
                                px: 2.5,
                                py: 0.8,
                                borderRadius: '4px',
                                textTransform: 'uppercase',
                                mb: 2.5,
                                '&:hover': { bgcolor: '#0f766e' }
                            }}
                        >
                            ADD MEDICINE
                        </Button>
                    )}

                    {/* Medicines Table */}
                    <Box sx={{ border: '1px solid #cbd5e1', borderRadius: '4px', overflow: 'hidden' }}>
                        {/* Header */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: '#eef2f6',
                            px: 2,
                            py: 1.5,
                            borderBottom: '1px solid #cbd5e1'
                        }}>
                            <Typography sx={{ width: '35%', fontWeight: 700, fontSize: '13px', color: '#1e293b', textAlign: 'center' }}>
                                Drug
                            </Typography>
                            <Typography sx={{ width: '18%', fontWeight: 700, fontSize: '13px', color: '#1e293b', textAlign: 'center' }}>
                                Duration (days)
                            </Typography>
                            <Typography sx={{ width: '18%', fontWeight: 700, fontSize: '13px', color: '#1e293b', textAlign: 'center' }}>
                                Frequency
                            </Typography>
                            <Typography sx={{ width: '12%', fontWeight: 700, fontSize: '13px', color: '#1e293b', textAlign: 'center' }}>
                                Quantity
                            </Typography>
                            <Typography sx={{ width: '17%', fontWeight: 700, fontSize: '13px', color: '#1e293b', textAlign: 'center' }}>
                                Special Instruction
                            </Typography>
                            <Box sx={{ width: '40px' }} />
                        </Box>

                        {/* Rows */}
                        {popupMedicines.map((med, index) => (
                            <Box
                                key={med.id || index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    px: 2,
                                    py: 1.5,
                                    borderBottom: '1px solid #f1f5f9',
                                    '&:last-child': { borderBottom: 'none' }
                                }}
                            >
                                {/* Drug */}
                                <Box sx={{ width: '35%' }}>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={med.drug || ''}
                                            disabled={isMedicalCompleted}
                                            onChange={(e) => handleUpdatePopupMedicine(index, 'drug', e.target.value)}
                                            displayEmpty
                                            sx={{ bgcolor: '#fff', fontSize: '13px' }}
                                        >
                                            <MenuItem value="">Select</MenuItem>
                                            {med.drug && !MEDICINE_OPTIONS.includes(med.drug) && (
                                                <MenuItem value={med.drug}>{med.drug}</MenuItem>
                                            )}
                                            {MEDICINE_OPTIONS.map((item) => (
                                                <MenuItem key={item} value={item}>{item}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* Duration (days) */}
                                <Box sx={{ width: '18%' }}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        disabled={isMedicalCompleted}
                                        value={med.durationDays || ''}
                                        onChange={(e) => handleUpdatePopupMedicine(index, 'durationDays', e.target.value)}
                                        sx={{ bgcolor: '#fff' }}
                                    />
                                </Box>

                                {/* Frequency */}
                                <Box sx={{ width: '18%' }}>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={med.frequency || ''}
                                            disabled={isMedicalCompleted}
                                            onChange={(e) => handleUpdatePopupMedicine(index, 'frequency', e.target.value)}
                                            displayEmpty
                                            sx={{ bgcolor: '#fff', fontSize: '13px' }}
                                        >
                                            <MenuItem value="">Select</MenuItem>
                                            <MenuItem value="Once a day">Once a day</MenuItem>
                                            <MenuItem value="Twice a day">Twice a day</MenuItem>
                                            <MenuItem value="Thrice a day">Thrice a day</MenuItem>
                                            <MenuItem value="Four times a day">Four times a day</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* Quantity */}
                                <Box sx={{ width: '12%' }}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        disabled={isMedicalCompleted}
                                        value={med.quantity || ''}
                                        onChange={(e) => handleUpdatePopupMedicine(index, 'quantity', e.target.value)}
                                        sx={{ bgcolor: '#f1f5f9' }}
                                    />
                                </Box>

                                {/* Special Instruction */}
                                <Box sx={{ width: '17%' }}>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={med.specialInstruction || ''}
                                            disabled={isMedicalCompleted}
                                            onChange={(e) => handleUpdatePopupMedicine(index, 'specialInstruction', e.target.value)}
                                            displayEmpty
                                            sx={{ bgcolor: '#fff', fontSize: '13px' }}
                                        >
                                            <MenuItem value="">Select</MenuItem>
                                            <MenuItem value="Before Food">Before Food</MenuItem>
                                            <MenuItem value="After Food">After Food</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>

                                {/* Trash Icon */}
                                {!isMedicalCompleted ? (
                                    <IconButton onClick={() => handleDeletePopupMedicineRow(index)} size="small" sx={{ color: '#2563eb' }}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                ) : (
                                    <Box sx={{ width: '28px' }} />
                                )}
                            </Box>
                        ))}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button
                        onClick={() => { setOpenMedicalModal(false); setSelectedMedicalItem(null); }}
                        variant="outlined"
                        sx={{
                            borderColor: '#cbd5e1',
                            color: '#475569',
                            fontWeight: 600,
                            fontSize: '13px',
                            textTransform: 'none'
                        }}
                    >
                        Close
                    </Button>
                    {!isMedicalCompleted && (
                        <Button
                            onClick={handleSavePopupMedicines}
                            variant="contained"
                            sx={{
                                bgcolor: '#2563eb',
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: '13px',
                                textTransform: 'uppercase',
                                px: 3,
                                '&:hover': { bgcolor: '#1d4ed8' }
                            }}
                        >
                            SAVE
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Follow Up Accordion */}
            <Accordion defaultExpanded sx={{ border: '1px solid #cbd5e1', borderRadius: '4px !important', boxShadow: 'none', mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '16px' }}>Follow Up</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2.5 }}>
                    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '4px', p: 2.5, bgcolor: '#fff' }}>
                        <Box sx={{ display: 'flex', borderBottom: '1px solid #e2e8f0', pb: 1, mb: 2 }}>
                            <Typography sx={{ width: '22%', fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>Follow-up Reason</Typography>
                            <Typography sx={{ width: '45%', fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>Follow-up Duration</Typography>
                            <Typography sx={{ width: '18%', fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>Due Date</Typography>
                            <Typography sx={{ width: '15%', fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>Follow-up to</Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {/* Follow-up Reason */}
                            <Box sx={{ width: '22%', pr: 2 }}>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    placeholder=""
                                    value={followupReason || ''}
                                    onChange={(e) => setFollowupReason && setFollowupReason(e.target.value)}
                                    sx={{ '& .MuiInput-underline:before': { borderBottomColor: '#cbd5e1' } }}
                                />
                            </Box>

                            {/* Follow-up Duration */}
                            <Box sx={{ width: '45%', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <TextField
                                    size="small"
                                    variant="outlined"
                                    value={durationDays || '0'}
                                    onChange={(e) => handleDurationChange && handleDurationChange('days', e.target.value)}
                                    sx={{
                                        width: '42px',
                                        bgcolor: '#fff',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '2px',
                                            '& fieldset': { borderColor: '#cbd5e1' }
                                        },
                                        '& .MuiInputBase-input': { textAlign: 'center', py: 0.6, px: 0.5 }
                                    }}
                                />
                                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', mr: 1 }}>Days</Typography>

                                <TextField
                                    size="small"
                                    variant="outlined"
                                    value={durationWeeks || '0'}
                                    onChange={(e) => handleDurationChange && handleDurationChange('weeks', e.target.value)}
                                    sx={{
                                        width: '42px',
                                        bgcolor: '#fff',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '2px',
                                            '& fieldset': { borderColor: '#cbd5e1' }
                                        },
                                        '& .MuiInputBase-input': { textAlign: 'center', py: 0.6, px: 0.5 }
                                    }}
                                />
                                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', mr: 1 }}>Weeks</Typography>

                                <TextField
                                    size="small"
                                    variant="outlined"
                                    value={durationMonths || '0'}
                                    onChange={(e) => handleDurationChange && handleDurationChange('months', e.target.value)}
                                    sx={{
                                        width: '42px',
                                        bgcolor: '#fff',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '2px',
                                            '& fieldset': { borderColor: '#cbd5e1' }
                                        },
                                        '& .MuiInputBase-input': { textAlign: 'center', py: 0.6, px: 0.5 }
                                    }}
                                />
                                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>Months</Typography>
                            </Box>

                            {/* Due Date */}
                            <Box sx={{ width: '18%', pr: 2 }}>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    placeholder="dd-mm-yyyy"
                                    value={dueDate || ''}
                                    onChange={(e) => setDueDate && setDueDate(e.target.value)}
                                    sx={{ '& .MuiInput-underline:before': { borderBottomColor: '#cbd5e1' } }}
                                />
                            </Box>

                            {/* Follow-up to */}
                            <Box sx={{ width: '15%' }}>
                                <FormControl variant="standard" sx={{ bgcolor: '#eeeeee', p: 0.5, borderRadius: '2px', width: '90px' }}>
                                    <Select
                                        fullWidth
                                        disableUnderline
                                        value={followupTo || 'Self'}
                                        onChange={(e) => setFollowupTo && setFollowupTo(e.target.value)}
                                        displayEmpty
                                        sx={{ fontWeight: 500, color: '#333', fontSize: '13.5px', '& .MuiSelect-select': { py: 0.3, px: 1 } }}
                                    >
                                        <MenuItem value="Self">Self</MenuItem>
                                        <MenuItem value="PHC">PHC</MenuItem>
                                        <MenuItem value="CHC">CHC</MenuItem>
                                        <MenuItem value="DH">DH</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                    </Box>
                </AccordionDetails>
            </Accordion>

            {/* Refer Accordion */}
            <Accordion defaultExpanded sx={{ border: '1px solid #cbd5e1', borderRadius: '4px !important', boxShadow: 'none', mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '16px' }}>Refer</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2.5 }}>
                    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '4px', p: 2.5, mb: 2.5, bgcolor: '#fff' }}>
                        {/* Header Row */}
                        <Box sx={{ display: 'flex', borderBottom: '1px solid #e2e8f0', pb: 1, mb: 2 }}>
                            <Typography sx={{ width: '22%', fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>Date</Typography>
                            <Typography sx={{ width: '45%', fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>Refer to</Typography>
                            <Typography sx={{ width: '33%', fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>Reason for Referral</Typography>
                        </Box>

                        {/* Input Row */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {/* Date Box */}
                            <Box sx={{ width: '22%', pr: 2 }}>
                                <Box sx={{ bgcolor: '#eeeeee', py: 0.8, px: 1.5, borderRadius: '2px', fontWeight: 500, color: '#333', fontSize: '13.5px', textAlign: 'center', width: '130px' }}>
                                    {dayjs(formData?.assessmentDate).format('DD-MM-YYYY')}
                                </Box>
                            </Box>

                            {/* Refer to Select */}
                            <Box sx={{ width: '45%', pr: 2 }}>
                                <FormControl variant="standard" sx={{ width: '140px' }}>
                                    <Select
                                        fullWidth
                                        value={referTo || ''}
                                        onChange={(e) => setReferTo && setReferTo(e.target.value)}
                                        displayEmpty
                                        sx={{
                                            fontSize: '13.5px',
                                            color: referTo ? '#1e293b' : '#94a3b8',
                                            '& .MuiInput-underline:before': { borderBottomColor: '#cbd5e1' }
                                        }}
                                    >
                                        <MenuItem value="" disabled sx={{ color: '#94a3b8' }}>Select</MenuItem>
                                        {(() => {
                                            const loggedData = JSON.parse(localStorage.getItem("loggedinUserdetails") || localStorage.getItem("NCD_loggedinUserdetails") || "{}");
                                            const { referFurtherToFacility } = getLocationFacilityDetails(loggedData);
                                            return referFurtherToFacility.map((fac) => (
                                                <MenuItem key={fac.id} value={String(fac.id)}>{fac.name}</MenuItem>
                                            ));
                                        })()}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Reason for Referral Select */}
                            <Box sx={{ width: '33%' }}>
                                <FormControl variant="standard" sx={{ width: '260px' }}>
                                    <Select
                                        fullWidth
                                        value={referReason || ''}
                                        onChange={(e) => setReferReason && setReferReason(e.target.value)}
                                        displayEmpty
                                        sx={{
                                            fontSize: '13.5px',
                                            color: referReason ? '#1e293b' : '#94a3b8',
                                            '& .MuiInput-underline:before': { borderBottomColor: '#cbd5e1' }
                                        }}
                                    >
                                        <MenuItem value="" disabled sx={{ color: '#94a3b8' }}>Reason for refer</MenuItem>
                                        <MenuItem value="Unable to diagnose completely">Unable to diagnose completely</MenuItem>
                                        <MenuItem value="Needs further tests">Needs further tests</MenuItem>
                                        <MenuItem value="Further Treatment">Further Treatment</MenuItem>
                                        <MenuItem value="The facility isn't conveniently located for the patient">The facility isn't conveniently located for the patient</MenuItem>
                                        <MenuItem value="Other">Other</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                    </Box>

                    {/* Referral Remarks */}
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>Referral Remarks</Typography>
                        <TextField
                            variant="standard"
                            fullWidth
                            placeholder=""
                            value={referralRemarks || ''}
                            onChange={(e) => setReferralRemarks && setReferralRemarks(e.target.value)}
                            sx={{
                                '& .MuiInput-underline:before': { borderBottomColor: '#cbd5e1' },
                                '& .MuiInput-underline:after': { borderBottomColor: '#3f51b5' }
                            }}
                        />
                    </Box>
                </AccordionDetails>
            </Accordion>

            {/* Save Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                    onClick={onSaveClick}
                    variant="contained"
                    sx={{
                        bgcolor: '#2563eb',
                        color: '#fff',
                        fontWeight: 700,
                        textTransform: 'none',
                        px: 4.5,
                        py: 1,
                        borderRadius: '4px',
                        '&:hover': { bgcolor: '#1d4ed8' }
                    }}
                >
                    SAVE
                </Button>
            </Box>
        </Box>
    );
};

export const BreastReviewTab = (props) => {
    const [phcMedicines, setPhcMedicines] = useState([
        { id: 1, drug: '', duration: '', quantity: '', frequency: '', specialInstruction: '' }
    ]);

    if (props.isDhOrChc) {
        return <DhChcReviewView {...props} />;
    }

    const {
        formData,
        screeningStatus,
        reviewObservations,
        setReviewObservations,
        medicines,
        addMedicineRow,
        removeMedicineRow,
        handleMedicineFieldChange,
        referTo,
        setReferTo,
        referReason,
        setReferReason,
        referralRemarks,
        setReferralRemarks,
        handleSaveReview,
        onSaveClick,
        setActiveWorkflowTab
    } = props;

    const activeMeds = (Array.isArray(medicines) && medicines.length > 0) ? medicines : phcMedicines;

    const handleAddMed = () => {
        if (addMedicineRow) {
            addMedicineRow();
        } else {
            setPhcMedicines(prev => [...prev, { id: Date.now(), drug: '', duration: '', quantity: '', frequency: '', specialInstruction: '' }]);
        }
    };

    const handleRemoveMed = (index) => {
        if (removeMedicineRow) {
            removeMedicineRow(index);
        } else {
            setPhcMedicines(prev => prev.filter((_, i) => i !== index));
        }
    };

    const handleUpdateMed = (index, field, value) => {
        if (handleMedicineFieldChange) {
            handleMedicineFieldChange(index, field, value);
        } else {
            setPhcMedicines(prev => {
                const updated = [...prev];
                updated[index] = { ...updated[index], [field]: value };
                return updated;
            });
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Status and Quick Links container */}
            <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, borderBottom: '1px solid #eef2f5', bgcolor: '#fafafa', mb: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: '15px' }}>
                            Status: {screeningStatus || 'Examined at PHC'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#3f51b5',
                            color: '#fff',
                            fontSize: '9px',
                            fontWeight: 'bold',
                            px: 0.5,
                            py: 0.2,
                            borderRadius: '2px',
                            lineHeight: 1
                        }}>
                            PDF
                        </Box>
                        <Typography sx={{ fontSize: '12px', color: '#3f51b5', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>
                            Quick Links
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* FOLLOW UP Card */}
                    <Box
                        onClick={() => setActiveWorkflowTab && setActiveWorkflowTab(3)}
                        sx={{
                            width: '95px',
                            height: '80px',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            bgcolor: '#fff',
                            transition: 'all 0.2s',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
                            '&:hover': { bgcolor: '#f8fafc', borderColor: '#3f51b5' }
                        }}
                    >
                        <img
                            src="/images/Foloowup.png"
                            alt="FOLLOW UP"
                            style={{ width: '32px', height: '32px', marginBottom: '4px', objectFit: 'contain' }}
                        />
                        <Typography sx={{ fontSize: '10px', fontWeight: 800, color: '#3f51b5' }}>
                            FOLLOW UP
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Review Observations Card */}
            <Box sx={{ bgcolor: '#fff', border: '1px solid #cbd5e1', borderRadius: '4px', p: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', pb: 1.5, mb: 2.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '16px' }}>
                        Review Observations
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                            variant="standard"
                            value={dayjs(formData?.assessmentDate).format('DD-MM-YYYY')}
                            InputProps={{
                                readOnly: true,
                                disableUnderline: true,
                                endAdornment: <CalendarTodayIcon sx={{ fontSize: '18px', color: '#64748b', ml: 1 }} />
                            }}
                            sx={{ width: '135px', '& .MuiInput-input': { fontWeight: 600, color: '#334155' } }}
                        />
                    </Box>
                </Box>
                <TextField
                    multiline
                    rows={4}
                    fullWidth
                    placeholder=""
                    value={reviewObservations || ''}
                    onChange={(e) => setReviewObservations && setReviewObservations(e.target.value)}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '4px',
                            bgcolor: '#fff',
                            '& fieldset': { borderColor: '#cbd5e1' },
                            '&:hover fieldset': { borderColor: '#94a3b8' },
                            '&.Mui-focused fieldset': { borderColor: '#3f51b5' }
                        }
                    }}
                />
            </Box>

            {/* Medication Advice Accordion */}
            <Accordion defaultExpanded sx={{ border: '1px solid #cbd5e1', borderRadius: '4px !important', boxShadow: 'none' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '16px' }}>Medication Advice</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                    <Box sx={{ border: '1px solid #cbd5e1', borderRadius: '4px', overflow: 'hidden', mb: 2 }}>
                        {/* Table Header */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: '#f8fafc',
                            px: 2,
                            py: 1.5,
                            borderBottom: '1px solid #cbd5e1'
                        }}>
                            <Typography sx={{ width: '30%', fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>
                                Drug
                            </Typography>
                            <Typography sx={{ width: '18%', fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>
                                Duration
                            </Typography>
                            <Typography sx={{ width: '18%', fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>
                                Quantity (in tablets)
                            </Typography>
                            <Typography sx={{ width: '15%', fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>
                                Frequency
                            </Typography>
                            <Typography sx={{ width: '15%', fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>
                                Special Instruction
                            </Typography>
                            <Box sx={{ width: '40px' }} />
                        </Box>

                        {/* Table Rows */}
                        {activeMeds.map((med, index) => (
                            <Box
                                key={med.id || index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    px: 2,
                                    py: 1.5,
                                    borderBottom: '1px solid #f1f5f9',
                                    '&:last-child': { borderBottom: 'none' }
                                }}
                            >
                                <Box sx={{ width: '30%' }}>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={med.drug || ''}
                                            onChange={(e) => handleUpdateMed(index, 'drug', e.target.value)}
                                            displayEmpty
                                            sx={{ bgcolor: '#fff', fontSize: '13px' }}
                                        >
                                            <MenuItem value="">Select</MenuItem>
                                            {med.drug && !MEDICINE_OPTIONS.includes(med.drug) && med.drug !== "Other" && (
                                                <MenuItem value={med.drug}>{med.drug}</MenuItem>
                                            )}
                                            {MEDICINE_OPTIONS.map((item) => (
                                                <MenuItem key={item} value={item}>{item}</MenuItem>
                                            ))}
                                            <MenuItem value="Other">Other</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Box sx={{ width: '18%' }}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={med.duration || med.durationDays || ''}
                                        onChange={(e) => handleUpdateMed(index, 'duration', e.target.value)}
                                        sx={{ bgcolor: '#fff' }}
                                    />
                                </Box>

                                <Box sx={{ width: '18%' }}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={med.quantity || ''}
                                        disabled
                                        sx={{ bgcolor: '#f1f5f9', '& .MuiOutlinedInput-root': { bgcolor: '#f1f5f9' } }}
                                    />
                                </Box>

                                <Box sx={{ width: '15%' }}>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={med.frequency || ''}
                                            onChange={(e) => handleUpdateMed(index, 'frequency', e.target.value)}
                                            displayEmpty
                                            sx={{ bgcolor: '#fff', fontSize: '13px' }}
                                        >
                                            <MenuItem value="">Select</MenuItem>
                                            <MenuItem value="Once a day">Once a day</MenuItem>
                                            <MenuItem value="Twice a day">Twice a day</MenuItem>
                                            <MenuItem value="Thrice a day">Thrice a day</MenuItem>
                                            <MenuItem value="Four times a day">Four times a day</MenuItem>
                                            <MenuItem value="As needed (PRN)">As needed (PRN)</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Box sx={{ width: '15%' }}>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={med.specialInstruction || med.specialInst || ''}
                                            onChange={(e) => handleUpdateMed(index, 'specialInstruction', e.target.value)}
                                            displayEmpty
                                            sx={{ bgcolor: '#fff', fontSize: '13px' }}
                                        >
                                            <MenuItem value="">Select</MenuItem>
                                            <MenuItem value="Before Food">Before Food</MenuItem>
                                            <MenuItem value="After Food">After Food</MenuItem>
                                            <MenuItem value="With Food">With Food</MenuItem>
                                            <MenuItem value="At Bedtime">At Bedtime</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Box sx={{ width: '40px', display: 'flex', justifyContent: 'center' }}>
                                    <IconButton onClick={() => handleRemoveMed(index)} size="small" sx={{ color: '#0284c7' }}>
                                        <DeleteIcon sx={{ fontSize: '18px' }} />
                                    </IconButton>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            onClick={handleAddMed}
                            variant="contained"
                            sx={{
                                bgcolor: '#00897b',
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: '13px',
                                px: 2.5,
                                py: 0.8,
                                borderRadius: '4px',
                                textTransform: 'uppercase',
                                '&:hover': { bgcolor: '#00796b' }
                            }}
                        >
                            + MEDICINE
                        </Button>
                    </Box>
                </AccordionDetails>
            </Accordion>

            {/* Refer Accordion */}
            <Accordion defaultExpanded sx={{ border: '1px solid #cbd5e1', borderRadius: '4px !important', boxShadow: 'none' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '16px' }}>Refer</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3 }}>
                    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '4px', p: 2, mb: 3 }}>
                        <Grid container spacing={2} sx={{ mb: 1, borderBottom: '1px solid #e2e8f0', pb: 1 }}>
                            <Grid item xs={12} md={3}>
                                <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>Date</Typography>
                            </Grid>
                            <Grid item xs={12} md={4.5}>
                                <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>Refer to</Typography>
                            </Grid>
                            <Grid item xs={12} md={4.5}>
                                <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>Reason for Referral</Typography>
                            </Grid>
                        </Grid>

                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={3}>
                                <Box sx={{ bgcolor: '#f1f5f9', p: 1, borderRadius: '4px', fontWeight: 600, color: '#475569', fontSize: '14px' }}>
                                    {dayjs(formData?.assessmentDate).format('DD-MM-YYYY')}
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4.5}>
                                <FormControl fullWidth variant="standard" sx={{ bgcolor: '#fff', border: '1px solid #cbd5e1', borderRadius: '4px', p: 0.5 }}>
                                    <Select
                                        fullWidth
                                        disableUnderline
                                        value={referTo || ''}
                                        onChange={(e) => setReferTo && setReferTo(e.target.value)}
                                        displayEmpty
                                        sx={{ fontWeight: 500, color: '#475569', '& .MuiSelect-select': { py: 0.5 } }}
                                    >
                                        <MenuItem value="">Select</MenuItem>
                                        <MenuItem value="3300">CHC</MenuItem>
                                        <MenuItem value="3400">DH</MenuItem>
                                        <MenuItem value="3500">TCC</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4.5}>
                                <FormControl fullWidth variant="standard" sx={{ bgcolor: '#fff', border: '1px solid #cbd5e1', borderRadius: '4px', p: 0.5 }}>
                                    <Select
                                        fullWidth
                                        disableUnderline
                                        value={referReason || ''}
                                        onChange={(e) => setReferReason && setReferReason(e.target.value)}
                                        displayEmpty
                                        sx={{ fontWeight: 500, color: '#475569', '& .MuiSelect-select': { py: 0.5 } }}
                                    >
                                        <MenuItem value="">Reason for refer</MenuItem>
                                        <MenuItem value="Unable to diagnose completely">Unable to diagnose completely</MenuItem>
                                        <MenuItem value="Needs further tests">Needs further tests</MenuItem>
                                        <MenuItem value="Further Treatment">Further Treatment</MenuItem>
                                        <MenuItem value="The facility isn't conveniently located for the patient">The facility isn't conveniently located for the patient</MenuItem>
                                        <MenuItem value="Other">Other</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Referral Remarks */}
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', mb: 1.5 }}>Referral Remarks</Typography>
                        <TextField
                            variant="standard"
                            multiline
                            rows={2}
                            fullWidth
                            placeholder=""
                            value={referralRemarks || ''}
                            onChange={(e) => setReferralRemarks && setReferralRemarks(e.target.value)}
                            sx={{
                                '& .MuiInput-underline:before': { borderBottomColor: '#cbd5e1' },
                                '& .MuiInput-underline:after': { borderBottomColor: '#3f51b5' }
                            }}
                        />
                    </Box>
                </AccordionDetails>
            </Accordion>

            {/* Save Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                    onClick={onSaveClick || handleSaveReview}
                    variant="contained"
                    sx={{
                        bgcolor: '#3b51a3',
                        color: '#fff',
                        fontWeight: 700,
                        textTransform: 'none',
                        px: 4.5,
                        py: 1,
                        borderRadius: '4px',
                        '&:hover': { bgcolor: '#2e3f80' }
                    }}
                >
                    SAVE
                </Button>
            </Box>
        </Box>
    );
};

export default BreastReviewTab;
